import Error from '@components/error';
import PasswordStrength from '@components/ui/password-strength';
import { useAppDispatch, useAppSelector } from '@hooks/app/redux';
import { useDocuments } from '@hooks/app/useDocuments';
import { AuthTitleSC } from '@pages/auth/auth.styled';
import Header from '@pages/auth/components/header';
import { actions } from '@store/ducks/auth';
import { Button, Form, Hyperlink, Loader, Paper, TextField } from '@ui';
import { formatIncompletePhoneNumber } from 'libphonenumber-js';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Prompt } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { formSchema, TFormValues, useInitialValues } from './step-form.schema';
import { StepFormSC } from './step-form.styled';

const StepForm: React.FC = () => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const registerData = useAppSelector((store) => store.auth.registerData);
  const isLoading = useAppSelector((store) => store.auth.registerLoading);
  const apiError = useAppSelector((store) => store.auth.registerError);
  const step = useAppSelector((store) => store.auth.currentRegStep);

  const initialValues = useInitialValues(registerData);
  const { documents, loading: documentsLoading } = useDocuments();

  const onSubmit = async (values: TFormValues) => {
    dispatch(actions.getRegisterRequest(values));
  };

  const [, formOptions] = Form.useForm({
    initialValues,
    validationSchema: formSchema,
    onSubmit,
    enableReinitialize: true,
  });

  return (
    <div style={{ paddingTop: 60 }} className="form-wrapper">
      <Header textId="reg.step_1.title" step={step} className="top" />
      <StepFormSC className="right-content">
        <div className="form">
          <AuthTitleSC>
            <FormattedMessage id="reg.step_1.form_title" />
          </AuthTitleSC>
          <Form {...formOptions} showFeedback={false} showLabel={false}>
            <Form.Custom>
              {(f) => (
                <Prompt
                  when={f.dirty}
                  message={formatMessage({ id: 'reg.step_1.leave_message' })}
                />
              )}
            </Form.Custom>
            <Form.Field name="firstName" label={formatMessage({ id: 'reg.step_1.name' })}>
              <TextField
                placeholder={`${formatMessage({ id: 'reg.step_1.name' })}*`}
                autoComplete="given-name"
              />
            </Form.Field>
            <Form.Field name="lastName" label={formatMessage({ id: 'reg.step_1.surname' })}>
              <TextField
                placeholder={`${formatMessage({ id: 'reg.step_1.surname' })}*`}
                autoComplete="family-name"
              />
            </Form.Field>
            <Form.Section label={formatMessage({ id: 'reg.step_1.contact' })} size="small">
              <Form.Field
                name="email"
                error={
                  apiError === 'used_main_identifier' &&
                  formatMessage({ id: `reg.step_1.auth_error.${apiError}` })
                }
                label={formatMessage({ id: 'reg.step_1.email' })}
              >
                <TextField
                  type="email"
                  autoComplete="email"
                  placeholder={`${formatMessage({ id: 'reg.step_1.email' })}*`}
                />
              </Form.Field>
            </Form.Section>
            <Form.Field
              name="phoneNumber"
              getValueFromEvent={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.currentTarget.value;
                const phone = formatIncompletePhoneNumber(value, 'SK');
                return phone ? phone : value;
              }}
              label={formatMessage({ id: 'reg.step_1.phone_number' })}
              caption={formatMessage({ id: 'reg.step_1.phone_number_caption' })}
            >
              <TextField
                type="phone"
                placeholder={`${formatMessage({ id: 'reg.step_1.phone_number' })}*`}
                autoComplete="tel"
              />
            </Form.Field>
            <Form.Section size="small" label={'Vytvorenie hesla'}>
              <Form.Field name="password" label={formatMessage({ id: 'reg.step_1.password' })}>
                <TextField
                  placeholder={`${formatMessage({ id: 'reg.step_1.password' })}*`}
                  type="password"
                  autoComplete="new-password"
                />
              </Form.Field>
              <Form.Custom>
                {(f) => (
                  <PasswordStrength password={f.values.password} style={{ marginBottom: 8 }} />
                )}
              </Form.Custom>
              <Form.Field name="repeated" label={formatMessage({ id: 'reg.step_1.repeated' })}>
                <TextField
                  placeholder={`${formatMessage({ id: 'reg.step_1.repeated' })}*`}
                  type="password"
                  autoComplete="new-password"
                />
              </Form.Field>
            </Form.Section>
            <Form.Custom>
              {(f) => (
                <Button
                  type="submit"
                  loading={f.isSubmitting || isLoading}
                  style={{
                    marginTop: theme.spacings.component.M,
                    marginBottom: theme.spacings.text.M,
                  }}
                >
                  {formatMessage({ id: 'reg.step_1.submit' })}
                </Button>
              )}
            </Form.Custom>
            {apiError && apiError !== 'used_main_identifier' && (
              <Error message={apiError} className="form-api-error" />
            )}
          </Form>
          <div className="agreements">
            <FormattedMessage
              id="reg.step_1.agree_general"
              tagName="span"
              values={{
                link1: (
                  <Hyperlink href={documents?.gdpr.file ?? '#'} target={'_blank'} rel="noreferrer">
                    <FormattedMessage id="reg.step_1.privacy_terms" />
                  </Hyperlink>
                ),
                link2: (
                  <Hyperlink href={documents?.vop.file ?? '#'} target={'_blank'} rel="noreferrer">
                    <FormattedMessage id="reg.step_1.general_terms" />
                  </Hyperlink>
                ),
                br: <br />,
              }}
            />
          </div>
        </div>
        {documentsLoading ? (
          <Paper
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
            }}
          >
            <Loader size="medium" loading />
          </Paper>
        ) : null}
      </StepFormSC>
    </div>
  );
};

export default StepForm;
